import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { SideNav } from '../components/SideNav';
import { useAppSelector } from '../hooks/useAppSelector';
import { CheckCircle2, ChevronRight, Clock, Shield } from 'lucide-react';
import { setCreationStep } from '../features/campaigns/campaignsSlice';
import { useAppDispatch } from '../hooks/useAppDipatch';
import { getCampaigns } from '../features/campaigns/campaignsThunk';
import { UserStatus } from '../types/user';

export const GuardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const campaigns = useAppSelector((state) => state.campaigns.campaigns);
  const hasActiveCampaign = campaigns.some((campaign: any) => campaign.status === 'active');
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);

  // Check if user is pending verification
  const isPendingVerification = user?.status === UserStatus.pending || user?.status === 'pending';
  const isApproved = user?.status === UserStatus.approved || user?.status === 'approved';

  // Only show onboarding banner on the dashboard home page
  const isDashboardPage = location.pathname === '/dashboard';

  useEffect(() => {
    if (user?.id) {
      dispatch(getCampaigns({ advertiser_id: user.id })).then(() => {
        setCampaignsLoaded(true);
      });
    }
  }, [dispatch, user?.id]);

  const handleStartCampaign = () => {
    dispatch(setCreationStep('campaign'));
    navigate('/campaigns');
  };

  return (
    <div className="min-h-screen">
      <div className="flex flex-col h-screen lg:flex-row">
        <SideNav />
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          {/* Pending Verification Banner - Shown for all pending users */}
          {isPendingVerification && (
            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4 sm:p-5 mb-6 backdrop-blur-sm">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="relative flex-shrink-0">
                    <div className="absolute inset-0 bg-amber-400/20 rounded-full animate-pulse" />
                    <div className="relative w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center border border-amber-500/40">
                      <Clock className="w-5 h-5 text-amber-400" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-semibold text-amber-200">Verification in Progress</span>
                      <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 rounded-full">
                        Up to 24 hours
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-amber-100/70">
                      Explore the dashboard and set up your campaigns now. They'll go live the moment you're approved.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-amber-300/80 bg-amber-500/10 px-3 py-2 rounded-lg border border-amber-500/20">
                  <Shield className="w-4 h-4" />
                  <span>Bar credentials under review</span>
                </div>
              </div>
            </div>
          )}

          {/* Welcome Banner - Only for approved users without active campaigns */}
          {isDashboardPage && campaignsLoaded && !hasActiveCampaign && isApproved && (
            <div className="bg-brand-surface border border-gray-700/50 rounded-lg p-6 mb-8 transition-all duration-500">
              <div className="flex flex-col gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 text-accent">
                    <CheckCircle2 size={14} strokeWidth={3} />
                    <span className="text-[10px] font-bold uppercase tracking-widest">
                      Verification Complete
                    </span>
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-neutral-light mb-2">
                    Let's find your <span className="text-accent">next case.</span>
                  </h2>
                  <p className="text-text-muted text-sm lg:text-base leading-relaxed">
                    Your firm profile is verified and active. Launch your first campaign to start
                    connecting with high-intent inquiries in your jurisdiction.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <button
                    onClick={handleStartCampaign}
                    className="px-6 py-3 bg-accent hover:bg-accent-hover text-brand-bg font-bold text-sm rounded-lg transition-all flex items-center gap-2 uppercase tracking-wider"
                  >
                    Start My First Campaign
                    <ChevronRight size={16} strokeWidth={3} />
                  </button>
                  <span className="text-xs text-text-muted font-medium uppercase tracking-widest">
                    Takes less than 2 minutes
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Pending User Welcome - Different CTA for pending users */}
          {isDashboardPage && campaignsLoaded && !hasActiveCampaign && isPendingVerification && (
            <div className="bg-brand-surface border border-gray-700/50 rounded-lg p-6 mb-8 transition-all duration-500">
              <div className="flex flex-col gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 text-accent">
                    <Clock size={14} strokeWidth={3} />
                    <span className="text-[10px] font-bold uppercase tracking-widest">
                      Get Started Now
                    </span>
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-neutral-light mb-2">
                    Set up while we <span className="text-accent">verify.</span>
                  </h2>
                  <p className="text-text-muted text-sm lg:text-base leading-relaxed">
                    Configure your targeting, set your bids, and have everything ready to launch the moment your credentials clear.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <button
                    onClick={handleStartCampaign}
                    className="px-6 py-3 bg-accent hover:bg-accent-hover text-brand-bg font-bold text-sm rounded-lg transition-all flex items-center gap-2 uppercase tracking-wider"
                  >
                    Configure My Campaign
                    <ChevronRight size={16} strokeWidth={3} />
                  </button>
                  <span className="text-xs text-text-muted font-medium uppercase tracking-widest">
                    Ready to launch once approved
                  </span>
                </div>
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
};
